import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Linkedin, Github } from 'lucide-react';
import './AboutPage.css';

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i = 0) => ({
        opacity: 1, y: 0,
        transition: { duration: 0.7, ease: 'easeOut', delay: i * 0.12 }
    })
};

const pillars = [
    {
        number: '01',
        title: 'Mission & Vision',
        body: 'We empower organizations by bridging complex technology and real-world business results. Our vision is a Southern Africa at the forefront of global digital innovation — powered by world-class engineering talent and strategic insight.'
    },
    {
        number: '02',
        title: 'Specialized Team',
        body: 'A collective of senior engineers, creative designers, and data strategists obsessed with technical quality. We bring together cloud architecture, full-stack development, and cybersecurity expertise — applied to every challenge we solve.'
    },
    {
        number: '03',
        title: 'Our Evolution',
        body: 'Founded in 2024 as a specialized consultancy, we rapidly evolved into a comprehensive digital partner for ambitious brands — scaling from local Zimbabwean startups to enterprises across Southern Africa and beyond.'
    },
    {
        number: '04',
        title: 'Engineering Standards',
        body: '"Innovative in every Byte" is our internal compass. Mandatory peer reviews, automated testing, security-by-default, and ISO-aligned best practices ensure every product we ship is built to perform, scale, and last.'
    }
];

const founder = {
    name: 'Brendon Karuru',
    role: 'Founder & CEO',
    bio: 'Visionary technologist driving Kodebuild\'s mission to deliver world-class digital solutions across Africa and beyond. With a deep passion for clean architecture and beautiful design, Brendon leads the engineering and creative teams to build products that make a real difference.',
    image: process.env.PUBLIC_URL + '/Leadership Images/Brendon Karuru.png',
    social: {
        linkedin: 'https://www.linkedin.com/in/brendon-karuru',
        github: '#',
    }
};

const companyStats = [
    { value: '2024', label: 'Founded' },
    { value: '10+', label: 'Projects Shipped' },
    { value: '30+', label: 'Happy Clients' },
    { value: '3', label: 'Countries Served' },
];

const AboutPage = () => {
    return (
        <motion.div
            className="about-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
            <Helmet>
                <title>About Us | Kodebuild — Engineering Excellence Since 2024</title>
                <meta name="description" content="Meet the team behind Kodebuild — engineering excellence since 2024. Our mission, values, and the people driving digital innovation across Africa." />
                <link rel="canonical" href="https://kodebuildtech.com/about" />
                {/* Open Graph */}
                <meta property="og:title" content="About Kodebuild | Engineering Excellence Since 2024" />
                <meta property="og:description" content="Meet the team behind Kodebuild — engineering excellence since 2024. Our mission, values, and the people driving digital innovation across Africa." />
                <meta property="og:url" content="https://kodebuildtech.com/about" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://kodebuildtech.com/Logo/kodebuild_png.png" />
                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="About Kodebuild | Engineering Excellence Since 2024" />
                <meta name="twitter:description" content="Meet the team behind Kodebuild — engineering excellence since 2024." />
                <meta name="twitter:image" content="https://kodebuildtech.com/Logo/kodebuild_png.png" />
                {/* Person JSON-LD */}
                <script type="application/ld+json">
                    {`
                    {
                      "@context": "https://schema.org",
                      "@type": "Person",
                      "name": "Brendon Karuru",
                      "jobTitle": "Founder & CEO",
                      "worksFor": {
                        "@type": "Organization",
                        "name": "Kodebuild",
                        "url": "https://kodebuildtech.com"
                      },
                      "url": "https://kodebuildtech.com/about",
                      "sameAs": ["https://www.linkedin.com/in/brendon-karuru"]
                    }
                    `}
                </script>
            </Helmet>

            {/* ── Hero ────────────────────────────────────── */}
            <section className="about-hero">
                <div className="about-hero-glow" />
                <div className="about-hero-glow-2" />
                <div className="container">
                    <motion.span
                        className="section-eyebrow"
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        Our Story
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15, duration: 0.8 }}
                    >
                        Engineering Excellence <br /><span className="text-gradient">Since 2024</span>
                    </motion.h1>
                    <motion.p
                        className="about-subtitle"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                    >
                        Kodebuild was founded on a simple principle: high-end software engineering should be
                        accessible to businesses that want to innovate. We bridge the gap between complex
                        technology and measurable business results.
                    </motion.p>
                </div>
            </section>

            {/* ── Company stats strip ─────────────────────── */}
            <section className="about-stats-strip">
                <div className="container">
                    <motion.div
                        className="about-stats-row"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: '-60px' }}
                        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
                    >
                        {companyStats.map((s, i) => (
                            <motion.div key={i} className="about-stat-item" variants={fadeUp}>
                                <span className="about-stat-value">{s.value}</span>
                                <span className="about-stat-label">{s.label}</span>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* ── Pillars grid ────────────────────────────── */}
            <section className="about-depth">
                <div className="container">
                    <motion.div
                        className="about-grid"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: '-60px' }}
                        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}
                    >
                        {pillars.map((p, i) => (
                            <motion.div key={i} className="about-card" variants={fadeUp} custom={i}>
                                <div className="about-card-number">{p.number}</div>
                                <h3>{p.title}</h3>
                                <p>{p.body}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* ── Leadership / Team ───────────────────────── */}
            <section className="about-team">
                <div className="about-team-bg" />
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUp}
                    >
                        <span className="section-eyebrow">The Vision</span>
                        <h2 className="section-title">Leadership</h2>
                        <p className="section-subtitle">
                            Guided by a commitment to world-class aesthetics and rigorous engineering standards,
                            we are building the digital future of Africa.
                        </p>
                    </motion.div>

                    <motion.div
                        className="leadership-showcase"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: '-60px' }}
                        variants={fadeUp}
                    >
                        <div className="founder-card">
                            <div className="founder-image-wrapper">
                                <img src={founder.image} alt={founder.name} className="founder-image" />
                                <div className="founder-image-overlay"></div>
                            </div>
                            
                            <div className="founder-content">
                                <div className="founder-header">
                                    <h3 className="founder-name">{founder.name}</h3>
                                    <span className="founder-role">{founder.role}</span>
                                </div>
                                <p className="founder-bio">{founder.bio}</p>
                                
                                <div className="founder-social">
                                    <a href={founder.social.linkedin} target="_blank" rel="noopener noreferrer" className="social-pill" aria-label="LinkedIn">
                                        <Linkedin size={18} />
                                        <span>Connect</span>
                                    </a>
                                    <a href={founder.social.github} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="GitHub">
                                        <Github size={20} />
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="join-us-card">
                            <div className="join-content">
                                <h3>Join the Mission</h3>
                                <p>We are always looking for exceptional engineers, designers, and strategists to join our growing team.</p>
                                <a href="/contact" className="btn-primary">View Opportunities</a>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    );
};

export default AboutPage;
